import React from "react";
import axios from "axios";
import { useSnackbar } from "notistack";
import { AppContext, User } from "../../../context/appContext";
import { endpoints } from "../../../endpoints/endpoints";
import IPagination from "../../../components/pagination/interface/iPagination";
import IActivityLogEntry from "../interface/iActivityLogEntry";
import IActivityLogFilter, { ALL_AREAS } from "../interface/iActivityLogFilter";
import IActivityLogSort, {
  ActivityLogSortColumn,
} from "../interface/iActivityLogSort";
import filterActivityLog from "../functions/filterActivityLog";
import sortActivityLog from "../functions/sortActivityLog";

export const ROWS_PER_PAGE = 25;
const DEFAULT_PERIOD_DAYS = 7;
const LOAD_ERROR_MESSAGE = "Protokoll konnte nicht geladen werden.";

export interface IActivityLogContext {
  entries: IActivityLogEntry[];
  visibleEntries: IActivityLogEntry[];
  pageEntries: IActivityLogEntry[];
  loading: boolean;
  /** The server returned only the newest entries of the period. */
  truncated: boolean;
  filter: IActivityLogFilter;
  updateFilter: (changes: Partial<IActivityLogFilter>) => void;
  sort: IActivityLogSort;
  toggleSort: (column: ActivityLogSortColumn) => void;
  pagination: IPagination;
  setPagination: React.Dispatch<React.SetStateAction<IPagination>>;
  search: () => Promise<void>;
}

interface IActivityLogResponse {
  meta: {
    status: "success" | "error";
    total: number;
    message: string | null;
    truncated?: boolean;
  };
  data: IActivityLogEntry[] | null;
}

export const ActivityLogContext = React.createContext<IActivityLogContext>(
  {} as IActivityLogContext
);

const toDateInputValue = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const createInitialFilter = (): IActivityLogFilter => {
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - DEFAULT_PERIOD_DAYS);

  return {
    start: toDateInputValue(start),
    end: toDateInputValue(today),
    roles: { TN: true, Coach: true, Admin: true, System: true, unknown: true },
    area: ALL_AREAS,
    status: "all",
    search: "",
  };
};

const ActivityLogProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element => {
  const {
    user,
    checkSession,
  }: { user: User; checkSession: (response: unknown) => void } =
    React.useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();

  const [entries, setEntries] = React.useState<IActivityLogEntry[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [truncated, setTruncated] = React.useState<boolean>(false);
  const [filter, setFilter] =
    React.useState<IActivityLogFilter>(createInitialFilter);
  const [sort, setSort] = React.useState<IActivityLogSort>({
    column: "date",
    direction: "desc",
  });
  const [pagination, setPagination] = React.useState<IPagination>({
    page: 1,
    rowsPerPage: ROWS_PER_PAGE,
  });
  const fetched = React.useRef<boolean>(false);

  const search = React.useCallback(async (): Promise<void> => {
    setLoading(true);

    try {
      const response = await axios.post<IActivityLogResponse>(
        endpoints.admin.activityLog,
        { sessionId: user.sessionId, start: filter.start, end: filter.end }
      );
      checkSession(response);

      if (response.data.meta.status === "success" && response.data.data) {
        setEntries(response.data.data);
        setTruncated(response.data.meta.truncated === true);
      } else {
        setEntries([]);
        setTruncated(false);
        enqueueSnackbar(response.data.meta.message ?? LOAD_ERROR_MESSAGE, {
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setEntries([]);
      setTruncated(false);
      enqueueSnackbar(LOAD_ERROR_MESSAGE, { variant: "error" });
    } finally {
      setPagination((prev) => ({ ...prev, page: 1 }));
      setLoading(false);
    }
  }, [checkSession, enqueueSnackbar, filter.end, filter.start, user.sessionId]);

  // Load once on mount like the other admin overviews; later loads run via "Suchen".
  React.useEffect(() => {
    if (!fetched.current) {
      fetched.current = true;
      search();
    }
  }, [search]);

  const updateFilter = (changes: Partial<IActivityLogFilter>): void => {
    setFilter((prev) => ({ ...prev, ...changes }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const toggleSort = (column: ActivityLogSortColumn): void => {
    setSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" }
    );
  };

  // Memoized because a year of log entries can be several thousand rows.
  const visibleEntries = React.useMemo(
    () => sortActivityLog(filterActivityLog(entries, filter), sort),
    [entries, filter, sort]
  );

  const pageStart = (pagination.page - 1) * pagination.rowsPerPage;
  const pageEntries = visibleEntries.slice(
    pageStart,
    pageStart + pagination.rowsPerPage
  );

  return (
    <ActivityLogContext.Provider
      value={{
        entries,
        visibleEntries,
        pageEntries,
        loading,
        truncated,
        filter,
        updateFilter,
        sort,
        toggleSort,
        pagination,
        setPagination,
        search,
      }}
    >
      {children}
    </ActivityLogContext.Provider>
  );
};

export default ActivityLogProvider;
